import _ from 'lodash';
import { getDiff, initDate, isValidDate } from '../../utils';
import { ModelType } from '../components';

export type ValidationFunction = {
  validator: (value: any, model?: ModelType) => boolean,
  error: string,
}

export type ValidationResult<T> = { [key in keyof Partial<T>]: string[] }

export const required: ValidationFunction = {
  validator: (value: any) => value !== '' && !_.isNil(value) && !_.isNaN(value),
  error: 'Required',
};

export const requiredWithMessage = (message: string = 'This field is required.'): ValidationFunction => ({
  validator: (value: any) => value !== '' && !_.isNil(value) && !_.isNaN(value),
  error: message,
});

export const maxLength = (length: number): ValidationFunction => ({
  validator: (value: string) => value.length <= length,
  error: `Maximum of ${length} characters allowed`,
});

export const minLength = (length: number): ValidationFunction => ({
  validator: (value: string) => value.length >= length,
  error: `Be a minimum of ${length} characters`,
});

export const exactLength = (length: number): ValidationFunction => ({
  validator: (value: string) => value.length === length,
  error: `Must be exactly ${length} characters`,
});

export const rangeLength = (min: number, max: number): ValidationFunction => ({
  validator: (value: string) => (value.length >= min && value.length <= max),
  error: `Must be exactly ${min} or ${max} characters`,
});

export const numeric: ValidationFunction = {
  validator: (value: string) => value === '' || /^\d+$/.test(value),
  error: 'Only numbers are allowed',
};

export const phone: ValidationFunction = {
  validator: (value: string) => value === '' || /^\+?[(]?\d{3}\)?[-\s.]?\d{3}[-\s.]?\d{4,6}$/im.test(value),
  error: 'Must be valid phone number',
};

export const email: ValidationFunction = {
  validator: (value: string) => value === '' || /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(value),
  error: 'Please enter a valid email address',
};

export const date = (format?: string): ValidationFunction => ({
  validator: (value: string) => value !== '' && isValidDate(value, format),
  error: 'Please enter a valid date',
});

export const oneUppercase: ValidationFunction = {
  validator: (value: string) => value === '' || /^(?=.*?[A-Z])/.test(value),
  error: 'Include at least one uppercase character',
};

export const oneLowercase: ValidationFunction = {
  validator: (value: string) => value === '' || /^(?=.*?[a-z])/.test(value),
  error: 'Include at least one lowercase character',
};

export const oneNumeric: ValidationFunction = {
  validator: (value: string) => value === '' || /^(?=.*?\d)/.test(value),
  error: 'Include at least one numeric',
};

export const oneSpecialCharacter: ValidationFunction = {
  validator: (value: string) => value === '' || /^(?=.*?[#?!@$%^&*-])/.test(value),
  error: 'Include at least one symbol',
};

export const alphanumeric = (message?: string): ValidationFunction => ({
  validator: (value: string) => value === '' || /^([A-Za-z\\\-' ])*$/.test(value),
  error: message ?? 'Invalid character entered',
});

export const fcaNumber: ValidationFunction[] = [numeric, rangeLength(6, 7)];

export const password: ValidationFunction[] = [minLength(12), oneUppercase, oneLowercase, oneNumeric, oneSpecialCharacter];

export const internationalPhone: ValidationFunction = {
  validator: (value: string) => value === '' || /^[A-Z]{2}:\+?\(?\d{1,3}\)?[-\s.]?\d{3}[-\s.]?\d{4,6}$/im.test(value),
  error: 'Please enter a valid telephone number',
};

export const maxNumber = (max: number | string): ValidationFunction => ({
  validator: (value: string) => {
    const $max: number = typeof max === 'string' ? +max.replace(/[^0-9.]+/g, '') : max;
    const $value: number = +value.replace(/[^0-9.-]+/g, '');
    return $value <= $max;
  },
  error: `Please enter a number less than ${max}`,
});

export const minNumber = (min: number | string): ValidationFunction => ({
  validator: (value: string) => {
    const $min: number = typeof min === 'string' ? +min.replace(/[^0-9.]+/g, '') : min;
    const $value: number = +value.replace(/[^0-9.-]+/g, '');
    return $value >= $min;
  },
  error: `Please enter a number greater than ${min}`,
});

export const rangeNumber = (min: number | string, max: number | string): ValidationFunction => ({
  validator: (value: string) => {
    const $max: number = typeof max === 'string' ? +max.replace(/[^0-9.-]+/g, '') : max;
    const $min: number = typeof min === 'string' ? +min.replace(/[^0-9.-]+/g, '') : min;
    const $value: number = typeof value === 'string' ? +value.replace(/[^0-9.-]+/g, '') : value;
    return $value >= $min && $value <= $max;
  },
  error: `Please enter a number between ${min} and ${max}`,
});

export const rangeAge = (min: number, max: number): ValidationFunction => ({
  validator: (value: string) => {
    const today = initDate().startOf('day');
    const valueDate = value ? initDate(new Date(value)).startOf('day') : initDate().startOf('day');
    const age = getDiff(today, valueDate, 'years', true);

    return !isValidDate(new Date(value)) || (age >= min && age <= max);
  },
  error: 'Candidate not eligible for Equifinance products.',
});

export const ukPostcode: ValidationFunction = {
  validator: (value: string) => {
    const regexA = /([Gg][Ii][Rr] 0[Aa]{2})/;
    const regex1 = /([A-Za-z]\d{1,2})/;
    const regex2 = /([A-Za-z][A-Ha-hJ-Yj-y]\d{1,2})/;
    const regex3 = /([A-Za-z]\d[A-Za-z])/;
    const regex4 = /([A-Za-z][A-Ha-hJ-Yj-y]\d[A-Za-z]?)/;
    const regex5 = /\s?\d[A-Za-z]{2}/;
    const regexPatterns = new RegExp(`(^${regexA.source}$|^(${regex1.source}|(${regex2.source}|(${regex3.source}|${regex4.source})))${regex5.source}$)`, 'g');
    return value === '' || regexPatterns.test(value);
  },
  error: 'Please enter a valid postal code',
};

export const securityAddressPostcode: ValidationFunction = {
  validator: (value: string) => {
    const prefixesToCheck = ['BT', 'JE', 'GY'];
    return !prefixesToCheck.some((prefix) => value.startsWith(prefix));
  },
  error: 'We\'re unable to process applications with this postal code',
};

export const mandatory = (message?: string): ValidationFunction => ({
  validator: (value: any) => value !== '' && !_.isNil(value) && !_.isNaN(value),
  error: message ?? 'Required',
});

export const requiredList = (message?: string): ValidationFunction => ({
  validator: (value: any) => !_.isNil(value) && !_.isEmpty(value),
  error: message ?? 'Required',
});

export const maxLengthWithMessage = (length: number, fieldName?: string): ValidationFunction => ({
  validator: (value: string) => value.length <= length,
  error: `The length of ${fieldName} must be ${length} characters or fewer`,
});

export const nonWhitespace = (message: string): ValidationFunction => ({
  validator: (value: string) => value === '' || /^(.*\S.*)$/g.test(value),
  error: `Please enter a ${message}`,
});
export const nonSpecial = (message: string): ValidationFunction => ({
  validator: (value: string) => value === '' || /\w+/g.test(value),
  error: message,
});

export const getErrors = (value: string, validatorArr: ValidationFunction[], model?: ModelType) => validatorArr.reduce(
  (prev, current) => [...prev, ...(current.validator(value, model) ? [] : [current.error])],
  [] as string[],
);
export const getFormErrors = (model: ModelType | undefined, validationRules: {
  [key: string]: ValidationFunction[],
}) => Object.entries(validationRules).reduce((prev: { [key: string]: string[] }, cur: [string, any]) => {
  const [key] = cur;
  const validators = validationRules?.[key] ?? [];
  const value = model?.[key];
  const errors = getErrors((typeof value === 'boolean' || typeof value === 'number') ? value : (value || ''), validators, model) ?? [];

  return {
    ...prev,
    [key]: errors,
  };
}, {});
